import React, { useContext, useEffect, useState, useRef } from "react";
import { FlexContainer, FormField, LottieLoadingIndicator, Toast } from "src/components";

import { ActionButton, TextLabel } from "./style";
import { getSettings, saveSettings, checkDomainStatus } from "../api/settingsApi";
import { LoadingIndicatorButton } from "src/scenes/Survey/components/Utils";
import DomainAuth from "./DomainAuth";
import SettingsDNS from "./SettingsDNS";
import SettingsSocialLinks from "./SettingsSocialLinks";
import { EmailCampaignsContext } from "../EmailCampaignsContext";
import styled from "styled-components";
import { BrowseFileButton } from "src/scenes/MyQuotes/components/style";
import attachImage from "src/img/new/attach-image.svg";
import { s3PresignedHeaderImageUrl } from "../api/newCampaignApi";

export const SenderProfileUploadCard = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
`;

export const CirclePreview = styled.div`
    width: 50px;
    height: 50px;
    border-radius: 93px;
    background-color: #FFFF;
    box-shadow: 0px 3px 6px #00000029;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;

    span {
        color: #808080;
    }
`;

export default function EmailCampaignsSettings() {
    const senderProfileFileInputRef = useRef(null);
    const { setInitialSettings } = useContext(EmailCampaignsContext);
    const [errors, setErrors] = useState({});
    const [senderName, setSenderName] = useState("");
    const [sendFromEmail, setSendFromEmail] = useState("");
    const [replyToEmail, setReplyToEmail] = useState("");
    const [settings, setSettings] = useState({});
    const [domainAuthenticated, setDomainAuthenticated] = useState(false);
    const [customDomainIsApplied, setCustomDomainIsApplied] = useState(false);
    const [senderDomainHasChanges, setSenderDomainHasChanges] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showDns, setShowDns] = useState(false);
    const [dnsIsLoading, setDnsIsLoading] = useState(false);
    const [senderProfileUrl, setSenderProfileUrl] = useState(""); // final URL or uploaded file URL
    const [senderProfileFile, setSenderProfileFile] = useState(null); // file only
    const [bimiVmcUrl, setBimiVmcUrl] = useState("");

    const fetchSettings = async () => {
        setIsLoading(true);
        try {
            const response = await getSettings();
            setSettings(response.settings);
            setReplyToEmail(response.settings?.replyToEmail || "");
            setSenderName(response.settings?.fromName || "");
            setDomainAuthenticated(Boolean(response.settings?.domain?.verifiedAt));
            setCustomDomainIsApplied(Boolean(response.settings?.domain));
            setInitialSettings(response.settings);
            setSendFromEmail(response.settings?.fromEmail);
            setSenderProfileUrl(response.settings?.profileImageUrl);
            setBimiVmcUrl(response.settings?.bimiVmcUrl || "");
        } catch (error) {
            console.log("Error: ", error);
            Toast.error("Error occurred while fetching settings.");
        } finally {
            setIsLoading(false);
        }
    };

    const revalidateDomain = async () => {
        setDnsIsLoading(true);
        try {
            const response = await checkDomainStatus();
            setDomainAuthenticated(response.verificationStatus === "success");
            if (response.domainError) {
                Toast.error(response.domainError);
            }
        } finally {
            setDnsIsLoading(false);
        }
    };

    useEffect(() => {
        fetchSettings();
    }, []);

    const onSenderNameChange = (e) => {
        setSenderName(e.target.value);
        setSenderDomainHasChanges(true);
        setErrors((prevErrors) => ({ ...prevErrors, senderName: "" }));
    };

    const onReplyToEmailChange = (e) => {
        setReplyToEmail(e.target.value);
        setSenderDomainHasChanges(true);
        setErrors((prevErrors) => ({ ...prevErrors, replyToEmail: "" }));
    };

    const onSendFromEmailChange = (e) => {
        setSendFromEmail(e.target.value);
        setSenderDomainHasChanges(true);
        setErrors((prevErrors) => ({ ...prevErrors, sendFromEmail: "" }));
    };

    const onCancelSenderDomainChanges = () => {
        setSenderName(settings.fromName || "");
        setReplyToEmail(settings.replyToEmail || "");
        setSendFromEmail(settings.fromEmail || "");
        setSenderDomainHasChanges(false);
    };

    const validate = () => {
        const newErrors = {};
        if (!senderName?.trim()) newErrors.senderName = "Sender's name is required.";
        if (sendFromEmail?.trim()) {
            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailPattern.test(sendFromEmail)) {
                newErrors.sendFromEmail = "Please enter a valid email address";
            }
        }
        if (replyToEmail?.trim()) {
            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailPattern.test(replyToEmail)) {
                newErrors.replyToEmail = "Please enter a valid email address";
            }
        }

        if (senderProfileUrl?.trim()) {
            try {
                const url = new URL(senderProfileUrl);
                if (!url.pathname.endsWith(".svg")) {
                    newErrors.senderProfileFile = "URL must point to an SVG file";
                }
            } catch {
                newErrors.senderProfileFile = "Please enter a valid URL";
            }
        }

        if (bimiVmcUrl?.trim()) {
            try {
                const url = new URL(bimiVmcUrl);
                if (!url.pathname.toLowerCase().endsWith(".pem")) {
                    newErrors.bimiVmcUrl = "BIMI VMC must be a .pem certificate URL";
                }
            } catch {
                newErrors.bimiVmcUrl = "Please enter a valid URL";
            }
        }

        return newErrors;
    };

    const onSubmitSenderDomainSettings = async (e) => {
        e.preventDefault();
        const newErrors = validate();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors); // display errors
            return;
        }
        setErrors({});
        try {
            setIsLoading(true);

            let finalSenderProfileUrl = "";
            if (senderProfileFile) {
                const { storedUrl } = await s3PresignedHeaderImageUrl(senderProfileFile, "svg", "SenderProfile");
                finalSenderProfileUrl = storedUrl;
            } else if (senderProfileUrl?.trim()) {
                finalSenderProfileUrl = senderProfileUrl.trim();
            }

            const payload = {
                fromName: senderName,
                fromEmail: sendFromEmail,
                replyToEmail: replyToEmail,
                profileImageUrl: finalSenderProfileUrl,
                bimiVmcUrl: bimiVmcUrl?.trim() || null
            };
            const response = await saveSettings(payload);
            if (response) Toast.success("Sender and domain has been successfully updated.");
            setSenderDomainHasChanges(false);
            fetchSettings();
        } catch (error) {
            console.log(error);
            Toast.error(error.getApiErrorDetails().message);
        } finally {
            setIsLoading(false);
        }
    };

    const onSenderProfileUrlChange = (e) => {
        setSenderProfileUrl(e.target.value);
        setSenderDomainHasChanges(true);
        setSenderProfileFile(null);
        setErrors((prevErrors) => ({ ...prevErrors, senderProfileFile: "" }));
    };

    const onRemoveSenderProfileFile = () => {
        if (senderProfileFile && senderProfileUrl) {
            URL.revokeObjectURL(senderProfileUrl);
        }

        setSenderProfileFile(null);
        setSenderProfileUrl("");
        setSenderDomainHasChanges(true);

        if (senderProfileFileInputRef.current) {
            senderProfileFileInputRef.current.value = "";
        }
    };

    const onBimiVmcUrlChange = (e) => {
        setBimiVmcUrl(e.target.value);
        setSenderDomainHasChanges(true);
        setErrors((prev) => ({ ...prev, bimiVmcUrl: "" }));
    };

    return (
        <>
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    height: "1490px",
                    border: "1px solid #D8DEEC",
                    borderRadius: "20px",
                    opacity: 1,
                    backgroundColor: "#FFFFFF",
                    overflow: "auto",
                }}
            >
                <div
                    style={{
                        borderBottom: "1px solid #D8DEEC",
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        height: "81px",
                        width: "100%",
                    }}
                >
                    <FlexContainer padding="0 2rem">
                        <div
                            style={{
                                fontStyle: "normal",
                                fontVariant: "normal",
                                fontWeight: "bold",
                                fontSize: "20px",
                                lineHeight: "24px",
                                fontFamily: "Manrope, sans-serif", // include fallback
                            }}
                        >
                            Settings
                        </div>
                    </FlexContainer>
                </div>
                { isLoading &&
                    <FlexContainer
                        justifyContent={isLoading && "center"}
                        alignItems="center"
                        color="#fff"
                        style={{
                            overflowX: "auto",
                            maxWidth: "98.625rem",
                            minHeight: "40rem",
                        }}
                    >
                        <LottieLoadingIndicator />
                    </FlexContainer>
                }
                { !isLoading &&
                    <>
                        <FlexContainer
                            style={{ marginTop: "0px", padding: "2rem", borderBottom: "1px solid #D8DEEC" }}
                            color="#F5F5F5"
                        >
                            <FlexContainer>
                                <TextLabel fontSize="16px" fontWeight="bold">
                                    Sender & Domain Settings
                                </TextLabel>
                                <TextLabel fontSize="15px" fontWeight="normal" color="#612684">
                                    Control your sender name, reply-to email, and domain authentication to
                                    ensure your emails are delivered reliably and reflect your business.
                                </TextLabel>
                            </FlexContainer>
                            <FlexContainer
                                style={{ boxShadow: "0px 5px 5px #0000001A" }}
                                border="1px solid #D8DEEC"
                                borderRadius="15px"
                                padding="20px"
                                color="#FFFFFF"
                                gap="10px"
                                margin="20px 0px 0px 0px"
                                maxWidth="839px"
                            >
                                <FlexContainer>
                                    <TextLabel fontSize="16px" fontWeight="bold">
                                        {"Sender's Name*"}
                                    </TextLabel>
                                    <TextLabel fontSize="15px" fontWeight="normal" color="#612684">
                                        {
                                            "Choose a sender’s name that will be recognisable, such as your business name."
                                        }
                                    </TextLabel>
                                    <FormField
                                        borderRadius="10px"
                                        size="15px"
                                        height="50px"
                                        border={errors.senderName ? "1px solid red" : "1px solid #D8DEEC"}
                                        backgroundColor={"#FFFFFF 0% 0% no-repeat padding-box"}
                                        boxShadow="0px 3px 6px #00000029"
                                        opacity="1"
                                        align="left"
                                        width="100%"
                                        type="text"
                                        placeholder={"Enter your business or organisations name"}
                                        placeholderColor="#808080"
                                        onChange={onSenderNameChange}
                                        showErrors={true}
                                        maxLength="100"
                                        value={senderName}
                                        name="categoryName"
                                        placeholderWeight="300"
                                    />
                                    <TextLabel color="red" style={{ marginTop: -10 }}>
                                        {errors.senderName}
                                    </TextLabel>
                                </FlexContainer>

                                <FlexContainer>
                                    <FlexContainer direction="row" gap="20px">
                                        <FlexContainer flexGrow={1}>    
                                            <TextLabel fontSize="16px" fontWeight="bold">
                                                Send from Email*
                                            </TextLabel>
                                            <TextLabel fontSize="15px" fontWeight="normal" color="#612684">
                                                Sent email campaigns are displayed from this email.
                                            </TextLabel>
                                            <FormField
                                                borderRadius="10px"
                                                size="15px"
                                                height="50px"
                                                border={errors.sendFromEmail ? "1px solid red" : "1px solid #D8DEEC"}
                                                backgroundColor={"#FFFFFF 0% 0% no-repeat padding-box"}
                                                boxShadow="0px 3px 6px #00000029"
                                                opacity="1"
                                                align="left"
                                                width="100%"
                                                type="text"
                                                placeholder={"Enter send from email here"}
                                                placeholderColor="#808080"
                                                onChange={onSendFromEmailChange}
                                                showErrors={true}
                                                maxLength="100"
                                                value={sendFromEmail}
                                                name="sendersName"
                                                placeholderWeight="300"
                                            />
                                            <TextLabel color="red" style={{ marginTop: -10 }}>
                                                {errors.sendFromEmail}
                                            </TextLabel>
                                        </FlexContainer>
                                        <FlexContainer flexGrow={1}>
                                            <TextLabel fontSize="16px" fontWeight="bold">
                                                Reply-To Email*
                                            </TextLabel>
                                            <TextLabel fontSize="15px" fontWeight="normal" color="#612684">
                                                Replies to this campaign will be sent to this email address.
                                            </TextLabel>
                                            <FormField
                                                borderRadius="10px"
                                                size="15px"
                                                height="50px"
                                                border={errors.replyToEmail ? "1px solid red" : "1px solid #D8DEEC"}
                                                backgroundColor={"#FFFFFF 0% 0% no-repeat padding-box"}
                                                boxShadow="0px 3px 6px #00000029"
                                                opacity="1"
                                                align="left"
                                                width="100%"
                                                type="text"
                                                placeholder={"Enter reply-to-email here"}
                                                placeholderColor="#808080"
                                                onChange={onReplyToEmailChange}
                                                showErrors={true}
                                                maxLength="100"
                                                value={replyToEmail}
                                                name="sendersName"
                                                maxWidth="410px"
                                                placeholderWeight="300"
                                            />
                                            <TextLabel color="red" style={{ marginTop: -10 }}>
                                                {errors.replyToEmail}
                                            </TextLabel>
                                        </FlexContainer>
                                    </FlexContainer>
                                    {senderDomainHasChanges && (
                                        <FlexContainer
                                            direction="row"
                                            tDirection="row"
                                            justifyContent="flex-end"
                                            alignItems="flex-end"
                                            maxWidth="820px"
                                            gap="20px"
                                        >
                                            <ActionButton
                                                height="39px"
                                                width="112px"
                                                border="1px solid #D8DEEC"
                                                backgroundColor="#FFFFFF"
                                                onClick={onCancelSenderDomainChanges}
                                                disabled={isLoading}
                                            >
                                                Cancel
                                            </ActionButton>
                                            {isLoading ? (
                                                <LoadingIndicatorButton height="39px" width="112px">
                                                    <LottieLoadingIndicator
                                                        height="2rem"
                                                        width="2rem"
                                                        margin="5px"
                                                        containerHeight="2rem"
                                                    />
                                                </LoadingIndicatorButton>
                                            ) : (
                                                <ActionButton
                                                    height="39px"
                                                    width="112px"
                                                    border="1px solid #171A22"
                                                    color="#FFFFFF"
                                                    onClick={onSubmitSenderDomainSettings}
                                                >
                                                    Save
                                                </ActionButton>
                                            )}
                                        </FlexContainer>
                                    )}
                                </FlexContainer>

                                <FlexContainer>
                                    <TextLabel fontSize="18px" fontWeight="bold">Sender Profile Image* 
                                        <span style={{ color: "#808080", fontSize: "14px", alignContent: "center", marginLeft: "6px" }}> (Optional) </span>
                                    </TextLabel>
                                    <TextLabel fontSize="15px" fontWeight="normal" color="#612684" >
                                        This is the profile image that appears in recipients’ inboxes and must be a square SVG.
                                        If you already have a publicly accessible SVG, you can provide the URL instead.
                                    </TextLabel>
                                    <TextLabel fontSize="13px" color="#808080" style={{ marginBottom: "20px", marginTop: "-8px" }}>
                                        Requirements:
                                        • SVG Tiny-PS
                                        • Square
                                        • Solid background
                                        • Max 32KB
                                    </TextLabel>
                                </FlexContainer>

                                <FlexContainer direction="row" style={{ marginTop: -14 }}>
                                    <FlexContainer flexGrow={1} direction="column" alignItems="left" width="180px">
                                        <TextLabel fontSize="16px" fontWeight="bold">Upload SVG file</TextLabel>
                                        <SenderProfileUploadCard>
                                            <CirclePreview>
                                                {senderProfileFile ? (
                                                    <img src={URL.createObjectURL(senderProfileFile)} width="50px" height="50px" alt="Sender Profile" />
                                                ) : senderProfileUrl ? (
                                                    <img src={senderProfileUrl} width="50px" height="50px" alt="Sender Profile" />
                                                ) : (
                                                    <img src={attachImage} width="30px" height="30px" alt="Sender Profile" />
                                                )}
                                            </CirclePreview>

                                            <BrowseFileButton
                                                backgroundColor="#000"
                                                color="#fff"
                                                border="1px solid #000"
                                                size="0.813rem"
                                                style={{
                                                    borderRadius: "7px",
                                                    boxShadow: "none", 
                                                    cursor: "pointer"
                                                }}
                                                onClick={() => senderProfileFileInputRef.current?.click()}
                                            >
                                                {senderProfileFile ? "Change File" : "Browse File"}
                                            </BrowseFileButton>

                                            {senderProfileFile && (
                                                <BrowseFileButton
                                                    size="0.813rem"
                                                    style={{ borderRadius: "7px", cursor: "pointer", background: "#FFF5F5", color: "#FF3B3B", border: "1px solid #FF3B3B" }}
                                                    onClick={onRemoveSenderProfileFile}
                                                > Remove </BrowseFileButton>
                                            )}

                                            <input
                                                ref={senderProfileFileInputRef}
                                                type="file"
                                                accept=".svg"
                                                style={{ display: "none" }}
                                                onChange={(e) => {
                                                    const file = e.target.files[0];
                                                    if (file && file.type === "image/svg+xml") {
                                                        setSenderProfileFile(file);
                                                        setSenderDomainHasChanges(true);
                                                        setSenderProfileUrl("");
                                                        setErrors((prevErrors) => ({ ...prevErrors, senderProfileFile: "" }));
                                                    } else {
                                                        Toast.error("Please upload a valid SVG file.");
                                                    }
                                                }}
                                            />
                                        </SenderProfileUploadCard>
                                        <TextLabel color="red">{errors.senderProfileFile}</TextLabel>
                                    </FlexContainer>
                                    <FlexContainer flexGrow={1} >
                                        <TextLabel fontSize="16px" fontWeight="bold">Or Enter Public SVG URL</TextLabel>
                                        <FormField
                                            borderRadius="10px"
                                            size="15px"
                                            height="50px"
                                            border="1px solid #D8DEEC"
                                            backgroundColor={"#FFFFFF"}
                                            boxShadow="0px 3px 6px #00000029"   
                                            opacity="1"
                                            align="left"
                                            width="100%"
                                            type="text"
                                            placeholder="https://example.com/your-logo.svg" 
                                            placeholderColor="#808080"
                                            value={senderProfileUrl}
                                            onChange={onSenderProfileUrlChange}
                                        />
                                    </FlexContainer>
                                </FlexContainer>

                                <FlexContainer style={{ marginBottom: "10px" }}>
                                    <TextLabel fontSize="16px" fontWeight="bold"> BIMI VMC Certificate URL
                                        <span style={{ color: "#808080", fontSize: "14px", marginLeft: "6px" }}>
                                            (Optional for most email clients; required for Gmail)</span>
                                    </TextLabel>
                                    <TextLabel fontSize="15px" fontWeight="normal" color="#612684">
                                        Provide the public URL to your BIMI Verified Mark Certificate (.pem).
                                    </TextLabel>

                                    <FormField
                                        borderRadius="10px"
                                        size="15px"
                                        height="50px"
                                        border={errors.bimiVmcUrl ? "1px solid red" : "1px solid #D8DEEC"}
                                        backgroundColor="#FFFFFF"
                                        boxShadow="0px 3px 6px #00000029"
                                        width="100%"
                                        align="left"
                                        type="text"
                                        placeholder="https://example.com/vmc.pem"
                                        value={bimiVmcUrl}
                                        onChange={onBimiVmcUrlChange}
                                    />

                                    <TextLabel color="red" style={{ marginTop: -10 }}>
                                        {errors.bimiVmcUrl}
                                    </TextLabel>
                                </FlexContainer>

                                {customDomainIsApplied &&
                                    <FlexContainer>
                                        <TextLabel fontSize="16px" fontWeight="bold">
                                            Authenticate Your Domain
                                        </TextLabel>
                                        <TextLabel fontSize="15px" fontWeight="normal" color="#612684">
                                            Authenticate your domain to send emails from your business address and improve
                                            email deliverability and trust.
                                        </TextLabel>
                                        <DomainAuth
                                            domainAuthenticated={domainAuthenticated}
                                            customDomainIsApplied={customDomainIsApplied}
                                            setShowDns={setShowDns}
                                            dnsIsLoading={dnsIsLoading}
                                            revalidateDomain={revalidateDomain}
                                        />
                                    </FlexContainer>
                                }
                            </FlexContainer>
                        </FlexContainer>
                        <SettingsSocialLinks settings={settings} fetchSettings={fetchSettings} />
                    </>
                }
            </div>
            {showDns && (
                <SettingsDNS replyToEmail={replyToEmail} settings={settings} setShowDns={setShowDns} />
            )}
        </>
    );
}
